import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Users, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Onglet Affiliations d'une page Club/Fédération.
 * V9.22 :
 *   - action réelle `accept`/`reject` (fix payload API)
 *   - noms humains privilégiés (source_name enrichi) — jamais d'UUID en UI
 *   - avatar + lien vers la fiche source
 */
export const EntityAffiliationsPanel = ({ entityType, entityId, canManage = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = { target_type: entityType, target_id: entityId, limit: 100 };
      const res = await api.get("/entity-links", { params });
      setItems(res.data?.items || res.data || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [entityType, entityId]);

  const review = async (linkId, decision) => {
    setBusy(linkId);
    try {
      // V9.22 fix : le backend attend { action: 'accept' | 'reject' }, pas { status }
      const action = decision === "accepted" ? "accept" : "reject";
      await api.patch(`/entity-links/${linkId}`, { action });
      toast.success(decision === "accepted" ? "Affiliation validée" : "Affiliation refusée");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la mise à jour");
    } finally {
      setBusy(null);
    }
  };

  const pending = items.filter((l) => l.status === "pending" || l.status === "declared");
  const accepted = items.filter((l) => l.status === "accepted");
  const rejected = items.filter((l) => l.status === "rejected");

  // V9.22 — Rendu humain d'une source (privilégier nom courant enrichi)
  const displayName = (l) => l.source_name || l.source_name_snapshot || "Personne inconnue";
  const displayInitials = (l) => {
    const name = displayName(l);
    return name.split(/\s+/).slice(0, 2).map((s) => s[0] || "").join("").toUpperCase() || "?";
  };
  const linkTo = (l) => {
    if (l.source_type === "athlete" && (l.source_slug || l.source_id)) {
      return `/athletes/${l.source_slug || l.source_id}`;
    }
    if (l.source_type === "coach" && (l.source_slug || l.source_id)) {
      return `/coaches/${l.source_slug || l.source_id}`;
    }
    if (l.source_type === "club" && (l.source_slug || l.source_id)) {
      return `/clubs/${l.source_slug || l.source_id}`;
    }
    return null;
  };

  const Section = ({ title, list, allowActions }) => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Users className="h-4 w-4" /> {title} ({list.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        {list.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Aucune demande.</p>
        ) : (
          <ul className="space-y-2">
            {list.map((l) => {
              const url = linkTo(l);
              const NameEl = (
                <div className="flex items-center gap-2 min-w-0">
                  <Avatar className="h-8 w-8 shrink-0">
                    {l.source_image && <AvatarImage src={l.source_image} alt="" />}
                    <AvatarFallback className="text-[10px]">{displayInitials(l)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{displayName(l)}</div>
                    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">
                      {l.source_type} · {(l.role || "").replaceAll("_", " ")}
                    </div>
                  </div>
                </div>
              );
              return (
                <li key={l.id} className="flex items-center justify-between border-b last:border-b-0 py-2 text-sm gap-2" data-testid={`affiliation-row-${l.id}`}>
                  {url ? (
                    <Link to={url} className="flex-1 min-w-0 hover:opacity-80" data-testid={`affiliation-link-${l.id}`}>
                      {NameEl}
                    </Link>
                  ) : (
                    <div className="flex-1 min-w-0">{NameEl}</div>
                  )}
                  {allowActions && canManage && (l.status === "pending" || l.status === "declared") && (
                    <div className="space-x-1 shrink-0">
                      <Button size="sm" variant="default" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busy === l.id} onClick={() => review(l.id, "accepted")} data-testid={`affiliation-accept-${l.id}`}>
                        {busy === l.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                        Valider
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy === l.id} onClick={() => review(l.id, "rejected")} data-testid={`affiliation-reject-${l.id}`}>
                        <XCircle className="h-3.5 w-3.5 mr-1" /> Refuser
                      </Button>
                    </div>
                  )}
                  {!(l.status === "pending" || l.status === "declared") && (
                    <Badge variant="outline" className="uppercase text-[10px] tracking-wide shrink-0">{l.status}</Badge>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-3" data-testid="entity-affiliations-panel">
      {loading ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <>
          <Section title="En attente" list={pending} allowActions />
          <Section title="Validées" list={accepted} allowActions={false} />
          {rejected.length > 0 && <Section title="Refusées" list={rejected} allowActions={false} />}
        </>
      )}
    </div>
  );
};
